import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Loader2 } from "lucide-react"
import { useToast } from "@/components/ui/use-toast"

type ImageCategory = {
  id: string
  name: string
  description: string
  prompts: {
    [key: string]: string // aspect ratio -> prompt template
  }
}

const imageCategories: ImageCategory[] = [
  {
    id: "event",
    name: "Event Promotion",
    description: "Create eye-catching visuals for your events",
    prompts: {
      "1:1": "Design a square social media post promoting a non-profit event. The image should showcase {description}. Use vibrant colors and modern design elements.",
      "4:3": "Create a professional and engaging event promotional image for a non-profit organization. The image should feature {description}. Use warm and inviting colors, incorporate elements that suggest community and impact.",
      "16:9": "Design a wide banner image for a non-profit event promotion. The image should highlight {description}. Include space for text overlay and use an engaging visual style.",
      "3:4": "Create a vertical promotional image for an event. The image should emphasize {description}. Use a composition that works well for mobile viewing and social stories."
    }
  },
  {
    id: "social",
    name: "Social Media",
    description: "Generate images for your social media posts",
    prompts: {
      "1:1": "Create a compelling square social media image for a non-profit organization. The image should illustrate {description}. Use a modern, clean design style with impactful visuals.",
      "4:3": "Design a social media image that captures {description}. Use a style that emphasizes community impact and engagement.",
      "16:9": "Create a wide social media banner that captures {description}. Use a professional and engaging design style suitable for non-profit communication.",
      "3:4": "Design a vertical social media post for Instagram. The image should showcase {description}. Use an inspiring and authentic visual style."
    }
  },
  {
    id: "impact",
    name: "Impact Stories",
    description: "Visualize your organization's impact",
    prompts: {
      "1:1": "Design a square image for sharing impact stories on social media. The image should depict {description}. Use a style that emphasizes human connection and positive change.",
      "4:3": "Create an emotional and impactful image that illustrates a non-profit success story. The image should show {description}. Use a photojournalistic style that captures authentic moments.",
      "16:9": "Create a wide banner image for showcasing non-profit impact. The image should capture {description}. Use a documentary style that tells a compelling story.",
      "3:4": "Design a vertical image for impact storytelling. The image should portray {description}. Focus on emotional connection and human elements."
    }
  },
  {
    id: "volunteer",
    name: "Volunteer Recruitment",
    description: "Create visuals for volunteer opportunities",
    prompts: {
      "1:1": "Create a square social media image for volunteer recruitment. The image should showcase {description}. Use warm colors and inclusive imagery.",
      "4:3": "Design an engaging image for volunteer recruitment. The image should represent {description}. Use an inspiring and welcoming visual style that encourages participation.",
      "16:9": "Design a wide banner for volunteer recruitment campaigns. The image should illustrate {description}. Use a style that emphasizes community and making a difference.",
      "3:4": "Create a vertical image for volunteer recruitment. The image should highlight {description}. Use imagery that inspires action and shows impact."
    }
  },
  {
    id: "fundraising",
    name: "Fundraising Campaigns",
    description: "Generate images for donation drives",
    prompts: {
      "1:1": "Design a square social media image for fundraising. The image should showcase {description}. Use impactful visuals that highlight the cause.",
      "4:3": "Create a compelling fundraising campaign image. The image should illustrate {description}. Use a professional and trustworthy visual style that encourages donations.",
      "16:9": "Create a wide banner for fundraising campaigns. The image should capture {description}. Use a style that builds trust and emotional connection.",
      "3:4": "Design a vertical fundraising campaign image. The image should convey {description}. Use visuals that create emotional resonance and inspire action."
    }
  }
]

const aspectRatios = [
  { id: "1:1", name: "Square", description: "Perfect for social media posts" },
  { id: "4:3", name: "Standard", description: "Great for general purpose images" },
  { id: "16:9", name: "Wide", description: "Ideal for website banners" },
  { id: "3:4", name: "Portrait", description: "Best for vertical layouts" }
]

export function GenerateImagePage({ profile, session }: { profile: any | null, session: any | null }) {
  const [selectedCategory, setSelectedCategory] = useState<ImageCategory>(imageCategories[0])
  const [aspectRatio, setAspectRatio] = useState("4:3")
  const [description, setDescription] = useState("")
  const [prompt, setPrompt] = useState("")
  const [generatedImage, setGeneratedImage] = useState<string | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [imageSettings, setImageSettings] = useState({
    style: 'modern',
    size: '1024x1024'
  })
  const { toast } = useToast()

  const generateImage = async () => {
    if (!description.trim()) {
      toast({
        title: "Description Required",
        description: "Please enter a description for your image",
        variant: "destructive"
      })
      return
    }

    if (!session?.access_token) {
      toast({
        title: "Authentication Error",
        description: "Please sign in to generate images",
        variant: "destructive"
      })
      return
    }

    setIsGenerating(true)
    setGeneratedImage(null)

    try {
      const prompt = selectedCategory.prompts[aspectRatio].replace(
        "{description}",
        description
      )

      if (!session?.access_token) {
        throw new Error('No access token available')
      }

      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/tools/image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          prompt,
          aspectRatio,
          enhancePrompt: true
        })
      })

      if (!response.ok) {
        throw new Error('Failed to generate image')
      }

      const data = await response.json()

      const imageUrl = data.data.imageUrl
      if (!imageUrl) throw new Error('No image data in response')

      setGeneratedImage(imageUrl)
      toast({
        title: "Success",
        description: "Image generated successfully!"
      })
    } catch (err) {
      console.error('Error:', err)
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to generate image",
        variant: "destructive",
      })
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold mb-2">Generate Images</h1>
      <p className="text-gray-600 mb-8">Create professional images for your non-profit organization</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div>
            <Label className="text-base">1. Select Image Category</Label>
            <div className="grid grid-cols-2 gap-4 mt-2">
              {imageCategories.map((category) => (
                <Card
                  key={category.id}
                  className={`p-4 cursor-pointer hover:border-primary transition-colors ${
                    selectedCategory.id === category.id ? "border-primary" : ""
                  }`}
                  onClick={() => setSelectedCategory(category)}
                >
                  <h3 className="font-semibold">{category.name}</h3>
                  <p className="text-sm text-gray-600">{category.description}</p>
                </Card>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-base">2. Choose Image Size</Label>
            <RadioGroup
              value={aspectRatio}
              onValueChange={setAspectRatio}
              className="grid grid-cols-2 gap-4 mt-2"
            >
              {aspectRatios.map((ratio) => (
                <div key={ratio.id}>
                  <RadioGroupItem
                    value={ratio.id}
                    id={ratio.id}
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor={ratio.id}
                    className="flex flex-col p-4 border rounded-md cursor-pointer hover:border-primary peer-data-[state=checked]:border-primary"
                  >
                    <span className="font-semibold">{ratio.name}</span>
                    <span className="text-sm text-gray-600">{ratio.description}</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div>
            <Label className="text-base">3. Describe Your Image</Label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full mt-2 p-2 border rounded-md"
              rows={4}
              placeholder="Describe what you want in your image..."
            />
          </div>

          <Button
            onClick={generateImage}
            disabled={isGenerating}
            className="w-full"
          >
            {isGenerating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isGenerating ? "Generating..." : "Generate Image"}
          </Button>
        </div>

        <div className="space-y-4">
          <Label className="text-base">Preview</Label>
          <div className="aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden">
            {generatedImage ? (
              <img
                src={generatedImage}
                alt="Generated"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                {isGenerating ? (
                  <Loader2 className="h-8 w-8 animate-spin" />
                ) : (
                  "Generated image will appear here"
                )}
              </div>
            )}
          </div>
          {generatedImage && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                const link = document.createElement('a')
                link.href = generatedImage
                link.download = `${selectedCategory.name}-${new Date().getTime()}.png`
                link.click()
              }}
            >
              Download Image
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default GenerateImagePage
